import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFreeAccess } from 'features/subscription/hooks/use-seats';
import { SubscriptionUsageMeter } from 'features/subscription/services/subscription-service';
import { WindowUsage } from 'features/usage/services/usage-service';
import { formatPercent, formatWindow } from 'features/usage/utils/usage-format';
import { Card, CardContent } from 'components/ui/card';
import { TopUpCoverNote } from 'features/credits/components/top-up-cover-note';
import { useTopUpCoversUsage } from 'features/credits/hooks/use-credits';
import { UsageBar } from './usage-bar';
import { UsageSectionLabel } from './usage-section-label';
import { UsageStatCard } from './usage-stat-card';
import { UsageWindowLimits } from './usage-window-limit';

// The section reports AI credits only; capacity meters such as knowledge folders and
// library storage belong to the plan, not to this period's consumption.
const METER_LABEL_KEYS: Record<string, string> = {
  'ai-credits': 'AI_CREDITS',
};

export function formatCreditValue(value: number, _scale?: number) {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value);
  return (rounded === 0 ? 0 : rounded).toLocaleString();
}

const CREDIT_FRACTION = { minimumFractionDigits: 0, maximumFractionDigits: 2 };

/** Top-up credits are spent in fractions: a whole balance reads 5,998, one with a fraction 5,997.62. */
export const formatCredits = (credits: number) =>
  (Number.isFinite(credits) ? credits : 0).toLocaleString(undefined, CREDIT_FRACTION);

/** Nothing is charged while enforcement is off, so there is no limit to draw: say so instead. */
export function UsageUnlimitedNotice() {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-section border border-grey-100 bg-card px-4 py-3 text-grey-600 dark:border-border dark:text-grey-300"
    >
      <Info aria-hidden="true" className="h-4 w-4 shrink-0" strokeWidth={1.8} />
      <p className="text-sm leading-5">
        {t('USAGE_UNLIMITED_ENFORCEMENT_OFF', {
          defaultValue:
            'Usage is unlimited for this organization. Usage enforcement is switched off, so no limits apply.',
        })}
      </p>
    </div>
  );
}

function MeterBlock({ meter, windows }: { meter: SubscriptionUsageMeter; windows: WindowUsage[] }) {
  const { t } = useTranslation();

  const title = t(METER_LABEL_KEYS[meter.meterKey], { defaultValue: 'AI Credits' });
  const hasOverage = meter.overage > 0;
  const usedPercent = meter.included > 0 ? (meter.used / meter.included) * 100 : 0;
  const topUpCovers = useTopUpCoversUsage();
  const isFull = usedPercent >= 100;
  const showCoverNote = topUpCovers && (isFull || windows.some((window) => window.percent >= 100));

  return (
    <div className="space-y-3">
      <Card className="rounded-section border-border/70 shadow-none">
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-foreground">{title}</span>
            <span className="text-xs text-muted-foreground">
              {formatWindow(meter.periodStartUtc, meter.periodEndUtc)}
            </span>
          </div>

          <div className="space-y-1.5">
            <UsageBar percent={usedPercent} warn={hasOverage} calm={topUpCovers && isFull} />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {t('CREDITS_USED_OF_INCLUDED', {
                  used: formatCreditValue(meter.used, meter.quantityScale),
                  included: formatCreditValue(meter.included, meter.quantityScale),
                  unit: meter.unitLabel,
                  defaultValue: '{{used}} of {{included}} {{unit}} used',
                })}
              </span>
              <span className="font-mono">{formatPercent(usedPercent, 2)}</span>
            </div>
          </div>

          {showCoverNote && <TopUpCoverNote />}

          <UsageWindowLimits windows={windows} calm={topUpCovers} />
        </CardContent>
      </Card>

      {/* <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <UsageStatCard
          label={t('INCLUDED', { defaultValue: 'Included' })}
          value={formatCreditValue(meter.included, meter.quantityScale)}
          subLabel={t('PER_BILLING_PERIOD', { defaultValue: 'Per billing period' })}
        />
        <UsageStatCard
          label={t('USED', { defaultValue: 'Used' })}
          value={formatCreditValue(meter.used, meter.quantityScale)}
          subLabel={t('CREDITS_PROJECTED', {
            value: formatCreditValue(meter.projection, meter.quantityScale),
            defaultValue: 'Projected: {{value}}',
          })}
        />
        <UsageStatCard
          label={t('REMAINING', { defaultValue: 'Remaining' })}
          value={formatCreditValue(meter.remaining, meter.quantityScale)}
          subLabel={
            meter.allowed
              ? t('WITHIN_ALLOWANCE', { defaultValue: 'Within allowance' })
              : t('ALLOWANCE_EXCEEDED', { defaultValue: 'Allowance exceeded' })
          }
          valueClassName={meter.remaining > 0 ? 'text-success-green' : 'text-warning-high-emphasis'}
        />
        <UsageStatCard
          label={t('OVERAGE', { defaultValue: 'Overage' })}
          value={formatCreditValue(meter.overage, meter.quantityScale)}
          subLabel={t('BEYOND_INCLUDED', { defaultValue: 'Beyond included' })}
          valueClassName={hasOverage ? 'text-warning-high-emphasis' : undefined}
        />
      </div> */}
    </div>
  );
}

export function UsageCredits({
  meters,
  userId,
  windows = [],
}: {
  meters: SubscriptionUsageMeter[];
  userId: string;
  /** The limit windows that hold for this payer, shown inside the credits card. */
  windows?: WindowUsage[];
}) {
  const { t } = useTranslation();
  const { isFreeAccess } = useFreeAccess();

  if (isFreeAccess) return <UsageUnlimitedNotice />;

  // The same meter comes once per scope -- the organization's sum under an empty userId, then
  // one row per person -- so the scope, not the key, tells the rows apart. Showing the wrong one
  // would report the whole organization's spend as a person's.
  const creditMeters = meters.filter(
    (meter) => meter.meterKey in METER_LABEL_KEYS && meter.userId === userId
  );

  if (!creditMeters.length) return null;

  return (
    <div className="space-y-4">
      {/* The organization label is hidden for now; restore:
          t('AI_CREDITS_ORGANIZATION', { defaultValue: 'AI credits (organization)' }) */}
      {userId && (
        <UsageSectionLabel>
          {t('AI_CREDITS_PERSONAL', { defaultValue: 'AI credits (this user)' })}
        </UsageSectionLabel>
      )}
      <div className="space-y-6">
        {creditMeters.map((meter, index) => (
          <MeterBlock
            key={`${meter.meterKey}:${meter.userId}`}
            meter={meter}
            windows={index === 0 ? windows : []}
          />
        ))}
      </div>
    </div>
  );
}
